import { useState, useEffect, useCallback, useRef, type PointerEvent as ReactPointerEvent } from "react";
import { useParams } from "react-router-dom";
import { blogService } from "../services/blogService";
import { useBlogs } from "../hooks/useBlogs";
import { usePageMeta } from "../hooks/usePageMeta";
import type { Blog, BlogRevision } from "../types/blog";
import { Button } from "../components/Button";
import { Navbar } from "../components/Navbar";
import { EditorPane, type EditorPaneHandle } from "../components/EditorPane";
import { PreviewPane } from "../components/PreviewPane";

import { MarkdownCheatsheet } from "./Editor/MarkdownCheatsheet";
import { BlogDetailsPane } from "./Editor/BlogDetailsPane";
import { AssetList } from "./Editor/AssetList";
import { RevisionList } from "./Editor/RevisionList";
import { BottomChat } from "../components/BottomChat";

import type { AIEdit } from "../types/edit";
import { applyEdit } from "../lib/edits";

const SIDEBAR_MIN = 280;
const SIDEBAR_MAX = 520;
const SIDEBAR_DEFAULT = 360;
const SIDEBAR_WIDTH_KEY = "editor-sidebar-width";

function readStoredSidebarWidth() {
    if (typeof window === "undefined") return SIDEBAR_DEFAULT;
    const stored = Number(localStorage.getItem(SIDEBAR_WIDTH_KEY));
    if (Number.isFinite(stored) && stored >= SIDEBAR_MIN && stored <= SIDEBAR_MAX) {
        return stored;
    }
    return SIDEBAR_DEFAULT;
}

export function Editor() {
    const { id } = useParams();
    const [markdown, setMarkdown] = useState("");
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [sidebarWidth, setSidebarWidth] = useState(readStoredSidebarWidth);
    const [isResizingSidebar, setIsResizingSidebar] = useState(false);
    const sidebarWidthRef = useRef(sidebarWidth);
    const editorRef = useRef<EditorPaneHandle>(null);
    const [activeSidebarPane, setActiveSidebarPane] = useState<string | null>("details");
    const [activeEdits, setActiveEdits] = useState<AIEdit[]>([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isAIChatOpen, setIsAIChatOpen] = useState(false);
    const [assetRefreshKey, setAssetRefreshKey] = useState(0);
    const [revisionRefreshKey, setRevisionRefreshKey] = useState(0);
    const [loadedRevisionId, setLoadedRevisionId] = useState<number | null>(null);

    usePageMeta({
        title: "Editor",
        path: id ? `/edit/${id}` : "/edit",
        noindex: true,
    });

    useEffect(() => {
        sidebarWidthRef.current = sidebarWidth;
    }, [sidebarWidth]);

    useEffect(() => {
        localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth));
    }, [sidebarWidth]);

    const handleSidebarResize = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        const startX = event.clientX;
        const startWidth = sidebarWidthRef.current;
        setIsResizingSidebar(true);
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";

        const onMove = (moveEvent: PointerEvent) => {
            const next = Math.min(
                SIDEBAR_MAX,
                Math.max(SIDEBAR_MIN, startWidth + (moveEvent.clientX - startX)),
            );
            setSidebarWidth(next);
        };

        const onUp = () => {
            setIsResizingSidebar(false);
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };

        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
    }, []);

    const togglePane = (pane: string) => {
        setActiveSidebarPane(prev => prev === pane ? null : pane);
    };

    const handleAcceptEdit = useCallback((editId: string) => {
        const edit = activeEdits.find(e => e.id === editId);
        if (!edit) return;

        setMarkdown(prev => applyEdit(prev, edit));
        setHasUnsavedChanges(true);
        setLoadedRevisionId(null);
        setActiveEdits(prev => prev.filter(e => e.id !== editId));
    }, [activeEdits]);

    const handleRejectEdit = useCallback((editId: string) => {
        setActiveEdits(prev => prev.filter(e => e.id !== editId));
    }, []);

    const handleAddEdits = useCallback((newEdits: AIEdit[]) => {
        setActiveEdits(prev => [...prev, ...newEdits]);
    }, []);

    const [blog, setBlog] = useState<Blog | null>(null);
    const { updateBlog, updateBlogContent } = useBlogs();

    const handleSave = useCallback(async () => {
        if (!id || markdown == null) return;
        try {
            await updateBlogContent(id, markdown);
            setHasUnsavedChanges(false);
            setBlog(prev => prev ? { ...prev, content: markdown } : null);
            setLoadedRevisionId(null);
            setRevisionRefreshKey(k => k + 1);
        } catch (error) {
            console.error("Failed to save blog content", error);
        }
    }, [id, markdown, updateBlogContent]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                handleSave();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleSave]);

    const handleContentChange = (newMarkdown: string) => {
        setMarkdown(newMarkdown);
        setHasUnsavedChanges(newMarkdown !== (blog?.content || ""));
        setLoadedRevisionId(null);
    };

    const handleLoadRevision = (revision: BlogRevision) => {
        setMarkdown(revision.content);
        setHasUnsavedChanges(revision.content !== (blog?.content || ""));
        setLoadedRevisionId(revision.id);
    };

    const insertMarkdown = useCallback((text: string) => {
        editorRef.current?.insertAtCursor(text);
    }, []);

    useEffect(() => {
        if (!id) return;

        const fetchBlog = async () => {
            try {
                const data = await blogService.getById(id);
                setBlog(data);
                setLoadedRevisionId(null);
                if (data.content) {
                    setMarkdown(data.content);
                }
            } catch (error) {
                console.error("Failed to fetch blog", error);
            }
        };

        fetchBlog();
    }, [id]);

    return (
        <div className="h-screen w-screen flex flex-col bg-base-200 overflow-hidden text-base-content relative">
            {/* Navbar / Toolbar */}
            <Navbar title={blog?.title} />

            {/* Main Layout Area */}
            <div className={`flex-1 flex overflow-hidden p-2 ${isSidebarOpen ? "gap-2" : "gap-0"}`}>
                {/* Sidebar */}
                <aside
                    className={`relative bg-base-100 rounded-2xl shadow-sm border border-base-300 flex flex-col gap-3 overflow-hidden shrink-0 ${isResizingSidebar ? "" : "transition-all duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)]"} ${isSidebarOpen ? "opacity-100" : "p-0 border-0 opacity-0"}`}
                    style={{
                        width: isSidebarOpen ? sidebarWidth : 0,
                        padding: isSidebarOpen ? 20 : 0,
                    }}
                >
                    <div className="flex gap-2 w-full shrink-0 items-center">
                        <Button
                            className="flex-1"
                            variant={hasUnsavedChanges ? "primary" : "soft"}
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                        <Button
                            className="flex-1"
                            variant={isAIChatOpen ? "primary" : "soft"}
                            onClick={() => setIsAIChatOpen(!isAIChatOpen)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                            </svg>
                            AI
                        </Button>
                        <Button
                            className="btn-ghost btn-sm btn-circle shrink-0"
                            onClick={() => setIsSidebarOpen(false)}
                            aria-label="Close Sidebar"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-5 h-5 stroke-current">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </Button>
                    </div>

                    <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-2">
                        {blog && (
                            <BlogDetailsPane
                                blog={blog}
                                onUpdate={updateBlog}
                                isOpen={activeSidebarPane === "details"}
                                onToggle={() => togglePane("details")}
                            />
                        )}
                        {id && (
                            <AssetList
                                blogId={id}
                                isOpen={activeSidebarPane === "assets"}
                                onToggle={() => togglePane("assets")}
                                refreshKey={assetRefreshKey}
                                onInsert={insertMarkdown}
                            />
                        )}
                        {id && (
                            <RevisionList
                                blogId={id}
                                isOpen={activeSidebarPane === "revisions"}
                                onToggle={() => togglePane("revisions")}
                                refreshKey={revisionRefreshKey}
                                loadedRevisionId={loadedRevisionId}
                                hasUnsavedChanges={hasUnsavedChanges}
                                onSelect={handleLoadRevision}
                            />
                        )}
                        <MarkdownCheatsheet
                            isOpen={activeSidebarPane === "cheatsheet"}
                            onToggle={() => togglePane("cheatsheet")}
                        />
                    </div>

                    {isSidebarOpen && (
                        <div
                            role="separator"
                            aria-orientation="vertical"
                            aria-label="Resize sidebar"
                            className="absolute top-0 right-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/50"
                            onPointerDown={handleSidebarResize}
                        />
                    )}
                </aside>

                {/* Content Area */}
                <div className="flex-1 overflow-hidden flex justify-center transition-all duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] gap-2">
                    {/* Editor Box */}
                    <div className="flex flex-col bg-base-100 rounded-2xl shadow-sm border border-base-300 overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] flex-1">
                        <div className="flex-1 overflow-hidden min-h-0 flex flex-col relative bg-base-100">
                            <EditorPane
                                ref={editorRef}
                                markdown={markdown}
                                onChange={handleContentChange}
                                showPreview={true}
                            />
                        </div>
                        <BottomChat
                            isOpen={isAIChatOpen}
                            setIsOpen={setIsAIChatOpen}
                            markdown={markdown}
                            onAddEdits={handleAddEdits}
                            blogId={id}
                            onAssetSaved={() => setAssetRefreshKey(k => k + 1)} />
                    </div>
                    {/* Preview Box */}
                    <div className="flex flex-col bg-base-100 rounded-2xl shadow-sm border border-base-300 overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] flex-1">
                        <div className="h-full min-w-[40vw]">
                            <PreviewPane
                                markdown={markdown}
                                activeEdits={activeEdits}
                                onAcceptEdit={handleAcceptEdit}
                                onRejectEdit={handleRejectEdit}
                            />
                        </div>
                    </div>
                </div>
            </div>


            {/* Floating Sidebar Toggle - Only visible when sidebar is closed */}
            <div className={`absolute bottom-6 left-6 z-50 transition-all duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] ${!isSidebarOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 pointer-events-none"}`}>
                <Button
                    className="btn-primary btn-circle shadow-lg"
                    onClick={() => setIsSidebarOpen(true)}
                    aria-label="Open Sidebar"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-6 h-6 stroke-current">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                    </svg>
                </Button>
            </div>
        </div >
    );
}
